import type React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import parseMarkdown from "./parseMarkdown";

const SCENE_WIDTH = 200; // Width of large scene nodes (circles)
const SCENE_HEIGHT = 100; // Height of large scene nodes (circles)
const ASSET_WIDTH = 50; // Small circle for interactions
const INTERACTION_FO_WIDTH = 50; // foreignObject height
const FO_HEIGHT = 200; // foreignObject height
const SPACING = 30; // Spacing between elements
const COLOR = "#00C889"; // Color for the nodes and lines

/**
 * Discreet "Scene #N" line rendered above each node title. Styles are inline
 * (not tailwind) because this subtree is serialized to a standalone SVG for the
 * PDF export, where class names would not resolve. #9ca3af is gray-400.
 */
const sceneNumberStyle: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: "normal",
  color: "#9ca3af",
  marginBottom: "2px",
};

const AIGeneratedGraph: React.FC<{ markdown: string; mdStringId?: string }> = ({
  markdown,
  mdStringId,
}) => {
  const { t } = useTranslation();
  const scenes = parseMarkdown(markdown);
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the scrollable container
  const nodes: JSX.Element[] = [];
  const links: JSX.Element[] = [];
  let xPosition = SPACING; // Starting position for the first scene
  let prevX = 0; // For linking lines between nodes, starting from 0

  // Previously: a wheel-event listener translated deltaY → scrollLeft and
  // called preventDefault, "hijacking" the page's vertical scroll the
  // moment the cursor crossed the graph. Removed so vertical wheel
  // bubbles up to the chat scroll container as expected. Horizontal
  // navigation still works via shift+wheel or by dragging the native
  // horizontal scrollbar rendered by `overflow-x: auto`.

  // Iterate over each scene and create the SVG nodes and links
  scenes.forEach((scene, sceneIndex) => {
    const sceneX = xPosition; // X position for the scene circle or triangle
    const sceneY = SCENE_HEIGHT / 2; // Centered on Y-axis

    // For the first scene, draw it as a triangle, others as circles
    if (sceneIndex === 0) {
      const triangleHeight = ((Math.sqrt(3) / 2) * SCENE_WIDTH) / 1.5; // Height of equilateral triangle

      nodes.push(
        <g key={`scene-${sceneIndex}`}>
          <polygon
            points={`${sceneX},${sceneY - triangleHeight / 2} ${sceneX},${
              sceneY + triangleHeight / 2
            } ${sceneX + SCENE_WIDTH / 1.5},${sceneY}`}
            fill={COLOR}
            stroke={COLOR}
            strokeWidth={2}
          />
          {/* Add the text below the triangle */}
          <foreignObject
            x={sceneX}
            y={SCENE_HEIGHT + 10}
            width={SCENE_WIDTH / 1.5}
            height={FO_HEIGHT}
          >
            <div
              style={{
                textAlign: "center",
                color: "black",
                fontWeight: "bold",
                wordWrap: "break-word",
                width: `${SCENE_WIDTH / 1.5}px`,
                whiteSpace: "normal",
              }}
            >
              <div style={sceneNumberStyle}>{t("general.sceneNumber", { n: sceneIndex + 1 })}</div>
              {scene.title.toUpperCase()}
            </div>
          </foreignObject>
        </g>,
      );
    } else {
      // Draw the scene circle (large circle for each scene after the first one)
      nodes.push(
        <g key={`scene-${sceneIndex}`}>
          <circle
            cx={sceneX + SCENE_WIDTH / 2}
            cy={sceneY}
            r={SCENE_HEIGHT / 2}
            fill={COLOR}
            stroke={COLOR}
            strokeWidth={2}
          />
          {/* Add the text below the circle */}
          <foreignObject x={sceneX} y={SCENE_HEIGHT + 10} width={SCENE_WIDTH} height={FO_HEIGHT}>
            <div
              style={{
                textAlign: "center",
                color: "black",
                fontWeight: "bold",
                wordWrap: "break-word",
                whiteSpace: "normal",
                overflow: "visible", // Ensure the text overflows if necessary
                height: "10vh",
              }}
            >
              <div style={sceneNumberStyle}>{t("general.sceneNumber", { n: sceneIndex + 1 })}</div>
              {scene.title.toUpperCase()}
            </div>
          </foreignObject>
        </g>,
      );
    }

    // Draw a line from the center of the previous element (prevX) to the center of the current scene
    if (sceneIndex > 0) {
      links.push(
        <line
          key={`line-prev-scene-${sceneIndex}`}
          x1={prevX} // End of previous element (centered)
          y1={sceneY}
          x2={sceneX + SCENE_WIDTH / 2} // Center of current element
          y2={sceneY}
          stroke={COLOR}
          strokeWidth={2}
        />,
      );
    }

    // Update the previous X position to the **center of the current circle or triangle**
    prevX = sceneX + SCENE_WIDTH / 2;

    // Draw interaction nodes (small circles) and lines connecting them to the scene
    scene.interactions.forEach((interaction, interactionIndex) => {
      const interactionX =
        xPosition + SCENE_WIDTH + SPACING + interactionIndex * (ASSET_WIDTH + SPACING);
      const interactionY = sceneY;

      nodes.push(
        <g key={`interaction-${sceneIndex}-${interactionIndex}`}>
          <circle
            cx={interactionX + ASSET_WIDTH / 2}
            cy={interactionY}
            r={ASSET_WIDTH / 2}
            fill={COLOR}
            stroke={COLOR}
            strokeWidth={2}
          />
          {/* Add the text below the interaction circle */}
          <foreignObject
            x={interactionX}
            y={interactionY + ASSET_WIDTH}
            width={INTERACTION_FO_WIDTH}
            height={FO_HEIGHT}
          >
            <div
              style={{
                textAlign: "center",
                color: "black",
                fontWeight: "bold",
                wordWrap: "break-word",
                fontSize: "10px",
                whiteSpace: "normal",
                overflow: "visible", // Ensure the text overflows if necessary
              }}
            >
              {interaction.toUpperCase()}
            </div>
          </foreignObject>
        </g>,
      );

      // Add the link from the scene circle to the interaction center
      links.push(
        <line
          key={`link-scene-${sceneIndex}-interaction-${interactionIndex}`}
          x1={sceneX + SCENE_WIDTH / 2} // Center of scene circle or triangle
          y1={interactionY}
          x2={interactionX + ASSET_WIDTH / 2} // Center of the interaction circle
          y2={interactionY}
          stroke={COLOR}
          strokeWidth={2}
        />,
      );
    });

    // Update xPosition for the next scene
    xPosition += SCENE_WIDTH + SPACING + scene.interactions.length * (ASSET_WIDTH + SPACING);
    prevX = xPosition - SPACING;
  });

  // Calculate the size of the SVG based on content
  // Extra space for the text and triangle top; the +16 over the historical 120
  // absorbs the "Scene #N" line now sitting above every node title.
  const svgHeight = SCENE_HEIGHT + 136;
  const svgWidth = xPosition + SPACING;

  // Use viewBox to ensure the entire content is visible without cropping
  return (
    <div ref={containerRef} style={{ overflowX: "auto", whiteSpace: "nowrap", maxWidth: "100%" }}>
      {/*
        `display: block` + horizontal `margin: auto` centers the graph when it
        is narrower than the container. Per CSS 2.1 §10.3.3, when the SVG is
        WIDER than the container (many scenes) the auto margins resolve to 0,
        so it pins left and stays fully scrollable — unlike text-align/flex
        centering, which would clip the unreachable left edge on overflow.
      */}
      <svg
        id={mdStringId ? `scenario-helper-svg-${mdStringId}` : undefined}
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight - 30}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", margin: "0 auto" }}
      >
        {links}
        {nodes}
      </svg>
    </div>
  );
};

export default AIGeneratedGraph;
